"use client"

import { useState, useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import Image from "next/image"
import { TextScramble } from "./text-scramble"
import { Sun, Moon } from "@nsmr/pixelart-react"

const navItems = [
  { num: "01", label: "FUTOVIA", href: "/" },
  { num: "02", label: "CONTACT", href: "/contact" },
]

function getSerial() {
  return "554"
}

// Get current month-year for batch with A1 suffix
function getBatch() {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const year = now.getFullYear()
  return `${month}-${year}-A1`
}

export function Navbar({ minimal = false }: { minimal?: boolean } = {}) {
  const pathname = usePathname()
  const [time, setTime] = useState("")
  const [isDark, setIsDark] = useState(true)
  const [isForceLight, setIsForceLight] = useState(false)
  const [countryCode, setCountryCode] = useState("XX")
  const [serial, setSerial] = useState("000")
  const [batch, setBatch] = useState("01-2026")
  const [hash, setHash] = useState("")
  const noiseCanvasRef = useRef<HTMLCanvasElement>(null)

  // Track URL hash for anchor-based nav items (e.g. /#pricing)
  useEffect(() => {
    const sync = () => setHash(window.location.hash)
    sync()
    window.addEventListener("hashchange", sync)
    return () => window.removeEventListener("hashchange", sync)
  }, [])

  // Determine active nav item based on pathname (+ hash for anchor links)
  const isActive = (href: string) => {
    const [path, h] = href.split("#")
    const targetPath = path || "/"
    if (h) return pathname === targetPath && hash === `#${h}`
    if (href === "/") return pathname === "/" && !hash
    return pathname.startsWith(href)
  }

  useEffect(() => {
    // Set serial and batch on client
    setSerial(getSerial())
    setBatch(getBatch())
    
    // Detect country from geolocation APIs with fallbacks
    const detectCountry = async () => {
      // Timezone to country mapping (comprehensive)
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      const tzCountryMap: Record<string, string> = {
        // Brazil
        "America/Sao_Paulo": "BR",
        "America/Rio_Branco": "BR",
        "America/Manaus": "BR",
        "America/Cuiaba": "BR",
        "America/Fortaleza": "BR",
        "America/Recife": "BR",
        "America/Bahia": "BR",
        "America/Belem": "BR",
        // Portugal
        "Europe/Lisbon": "PT",
        "Atlantic/Madeira": "PT",
        "Atlantic/Azores": "PT",
        // Singapore
        "Asia/Singapore": "SG",
        // UK
        "Europe/London": "GB",
        // US
        "America/New_York": "US",
        "America/Chicago": "US",
        "America/Denver": "US",
        "America/Los_Angeles": "US",
        "America/Phoenix": "US",
        "Pacific/Honolulu": "US",
        // Others
        "Asia/Tokyo": "JP",
        "Europe/Paris": "FR",
        "Europe/Berlin": "DE",
        "Australia/Sydney": "AU",
        "Australia/Melbourne": "AU",
        "Europe/Madrid": "ES",
        "Europe/Rome": "IT",
        "Europe/Amsterdam": "NL",
        "Asia/Hong_Kong": "HK",
        "Asia/Seoul": "KR",
        "Asia/Shanghai": "CN",
        "America/Toronto": "CA",
        "America/Vancouver": "CA",
        "America/Mexico_City": "MX",
        "America/Argentina/Buenos_Aires": "AR",
      }
      
      // First try timezone detection (instant, no network)
      if (tzCountryMap[tz]) {
        setCountryCode(tzCountryMap[tz])
        return
      }
      
      // Then try IP geolocation APIs
      try {
        // Try ip-api.com (no API key needed, generous limits)
        const res = await fetch("http://ip-api.com/json/?fields=countryCode")
        const data = await res.json()
        if (data.countryCode) {
          setCountryCode(data.countryCode)
          return
        }
      } catch {
        // Silent fail
      }
      
      try {
        // Fallback to ipapi.co
        const res = await fetch("https://ipapi.co/country_code/")
        const code = await res.text()
        if (code && code.length === 2) {
          setCountryCode(code.trim())
          return
        }
      } catch {
        // Silent fail, keep XX
      }
    }
    detectCountry()
  }, [])

  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      setTime(
        now.toLocaleTimeString("en-US", {
          hour12: false,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      )
    }
    updateTime()
    const interval = setInterval(updateTime, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    // If page has force-light, respect it
    const forceLight = document.querySelector(".force-light") !== null
    if (forceLight) {
      setIsForceLight(true)
      setIsDark(false)
      document.documentElement.classList.remove("dark")
      return
    }

    const saved = localStorage.getItem("theme")
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches

    // No saved preference -> follow system. Saved value wins.
    const useDark = saved ? saved === "dark" : prefersDark

    if (useDark) {
      setIsDark(true)
      document.documentElement.classList.add("dark")
    } else {
      setIsDark(false)
      document.documentElement.classList.remove("dark")
    }
  }, [])

  const toggleTheme = () => {
    const newIsDark = !isDark
    setIsDark(newIsDark)

    if (newIsDark) {
      document.documentElement.classList.add("dark")
      localStorage.setItem("theme", "dark")
    } else {
      document.documentElement.classList.remove("dark")
      localStorage.setItem("theme", "light")
    }
  }

  // Navbar noise effect
  useEffect(() => {
    const canvas = noiseCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    let animationId: number
    let lastTime = 0
    const fps = 24

    const resize = () => {
      const header = canvas.parentElement
      if (header) {
        canvas.width = Math.ceil(header.offsetWidth / 2)
        canvas.height = Math.ceil(header.offsetHeight / 2)
      }
    }

    const noise = (timestamp: number) => {
      if (timestamp - lastTime < 1000 / fps) {
        animationId = requestAnimationFrame(noise)
        return
      }
      lastTime = timestamp

      const imageData = ctx.createImageData(canvas.width, canvas.height)
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        const value = Math.random() * 50
        data[i] = value
        data[i + 1] = value
        data[i + 2] = value
        data[i + 3] = 255
      }

      ctx.putImageData(imageData, 0, 0)
      animationId = requestAnimationFrame(noise)
    }

    resize()
    animationId = requestAnimationFrame(noise)
    window.addEventListener("resize", resize)

    return () => {
      window.removeEventListener("resize", resize)
      cancelAnimationFrame(animationId)
    }
  }, [])

  const isCaseStudyPage = pathname.startsWith("/work/") && pathname !== "/work"

  return (
    <header className="fixed left-0 right-0 top-0 z-[60] bg-background/95 px-6 py-4 backdrop-blur-sm md:px-12 md:pt-8">
      {/* Navbar noise overlay */}
      <canvas
        ref={noiseCanvasRef}
        className="pointer-events-none absolute inset-0 h-full w-full opacity-0 dark:opacity-[0.18]"
        aria-hidden="true"
      />
      {/* Row 1: Logo + Nav items + theme toggle */}
      <div className="flex items-center justify-between">
        {/* Logo + Nav items */}
        <div className="flex items-center gap-6">
          <a href="/">
            <Image
              src={isDark ? "/logo.svg" : "/logo-light.svg"}
              alt="Futovia"
              width={24}
              height={24}
              className={`h-6 w-6 ${isDark ? "drop-shadow-[0_0_8px_var(--primary)]" : ""}`}
            />
          </a>
          {!minimal && navItems.map((item) => (
            <a
              key={item.num}
              href={item.href}
              className={`inline-flex items-center gap-1.5 font-sans text-base uppercase tracking-wide transition-colors ${
                isActive(item.href)
                  ? "bg-foreground px-2 py-0.5 text-background"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="hidden font-mono text-[10px] leading-none tracking-wider opacity-60 sm:inline">{item.num}</span>
              <TextScramble text={item.label} delay={100 * parseInt(item.num)} randomInterval={45000} speed={90} />
            </a>
          ))}
        </div>

        {/* Right side - toggle only (hidden when force-light) */}
        {!isForceLight && (
          <button
            onClick={toggleTheme}
            className="flex h-8 w-8 items-center justify-center border border-border text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Row 2: Tech info decorative row */}
      {!minimal && <div className="mt-3 flex flex-wrap items-center justify-between gap-4 font-mono text-[11px] uppercase tracking-wider">
        {/* Left side - Japanese with glow */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <span className={`text-xs text-primary ${isDark ? "[text-shadow:0_0_10px_var(--primary),0_0_20px_var(--primary),0_0_30px_var(--primary)]" : ""}`}>
              <TextScramble text="将来の道" delay={200} scrambleOnHover />
            </span>
          </div>
          <div className="hidden text-muted-foreground/60 sm:block">
            <span className="text-muted-foreground/40">UEN:</span> 202210487K
          </div>
          <div className="hidden text-muted-foreground/60 md:block">
            MAINFRAME ONLINE
          </div>
        </div>

        {/* Right side - Technical data */}
        <div className="flex items-center gap-4">
          <div className="hidden text-muted-foreground/60 lg:block">
            <span className="text-muted-foreground/40">BATCH:</span> NEB-2199-MK1
          </div>
          <div className="hidden text-muted-foreground/60 sm:block">
            <span className="text-muted-foreground/40">SN:</span> FTV-{serial}-{countryCode}
          </div>
          <div className="hidden text-muted-foreground/60 sm:block">
            <span className="text-muted-foreground/40">EST:</span> 2021-2026
          </div>
          <div className="text-muted-foreground/60">
            <span className="text-muted-foreground/40">SYS_TIME:</span> {time}
          </div>
          {/* CTA Button with ASCII brackets */}
          <a
            href="/contact"
            className={`group relative bg-transparent px-1 py-1 text-[11px] text-primary transition-colors hover:text-primary/80 ${isDark ? "[text-shadow:0_0_10px_var(--primary),0_0_20px_var(--primary),0_0_30px_var(--primary)]" : ""}`}
          >
            <span className="hidden sm:inline">{"[ "}</span>
            <TextScramble text="GET IN TOUCH" delay={600} randomInterval={18000} />
            <span className="hidden sm:inline">{" ]"}</span>
          </a>
          <a
            href="https://billing.stripe.com/p/login/8wMcMN3vkbMqehqfYY"
            target="_blank"
            rel="noopener noreferrer"
            className="relative bg-transparent px-1 py-1 text-[11px] text-muted-foreground/60 transition-colors hover:text-foreground"
          >
            <TextScramble text="LOGIN" delay={700} randomInterval={22000} />
          </a>
        </div>
      </div>}

      {/* Neon bar - hidden on case study pages */}
      {!isCaseStudyPage && (
        <div className={`mt-4 h-px w-full bg-primary ${isDark ? "shadow-[0_0_10px_var(--primary),0_0_20px_var(--primary)]" : ""}`} />
      )}
    </header>
  )
}
